import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

interface TalentFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disciplines: string[];
  discipline: string;
  onDisciplineChange: (v: string) => void;
  location: string;
  onLocationChange: (v: string) => void;
  openToRelocation: boolean;
  onOpenToRelocationToggle: () => void;
  activelySeeking: boolean;
  onActivelySeekingToggle: () => void;
  onClearAll: () => void;
}

function FilterPill({
  active,
  onClick,
  activeClassName,
  children,
}: {
  active: boolean;
  onClick: () => void;
  activeClassName: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-8 px-3 rounded-full text-xs font-semibold border transition-colors font-sans",
        active
          ? activeClassName
          : "bg-transparent border-white/10 text-zinc-500 hover:border-white/20 hover:text-zinc-300",
      )}
    >
      {children}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 font-sans">
      {children}
    </p>
  );
}

export function TalentFilterDrawer({
  open,
  onOpenChange,
  disciplines,
  discipline,
  onDisciplineChange,
  location,
  onLocationChange,
  openToRelocation,
  onOpenToRelocationToggle,
  activelySeeking,
  onActivelySeekingToggle,
  onClearAll,
}: TalentFilterDrawerProps) {
  const hasActive =
    discipline !== "all" ||
    location.trim() !== "" ||
    openToRelocation ||
    activelySeeking;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col w-80 max-w-[88vw] bg-zinc-950 border-l border-white/10 p-0 gap-0"
      >
        <SheetHeader className="flex-none px-6 py-5 border-b border-white/10">
          <SheetTitle
            className="text-sm font-bold uppercase tracking-widest text-white"
            style={{ fontFamily: "var(--font-space-grotesk, 'Space Grotesk', sans-serif)" }}
          >
            Filter Talent
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
          {disciplines.length > 0 && (
            <div>
              <SectionLabel>Discipline</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {disciplines.map((d) => (
                  <FilterPill
                    key={d}
                    active={discipline === d}
                    onClick={() => onDisciplineChange(discipline === d ? "all" : d)}
                    activeClassName="bg-primary/20 border-primary/40 text-primary"
                  >
                    {d}
                  </FilterPill>
                ))}
              </div>
            </div>
          )}

          <div>
            <SectionLabel>Location</SectionLabel>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
              <Input
                placeholder="City, country..."
                className="pl-9 bg-zinc-900/50 border-white/10 focus-visible:ring-primary/50 text-white font-sans"
                value={location}
                onChange={(e) => onLocationChange(e.target.value)}
              />
            </div>
          </div>

          <div>
            <SectionLabel>Availability</SectionLabel>
            <div className="flex flex-wrap gap-2">
              <FilterPill
                active={openToRelocation}
                onClick={onOpenToRelocationToggle}
                activeClassName="bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
              >
                Open to Relocation
              </FilterPill>
              <FilterPill
                active={activelySeeking}
                onClick={onActivelySeekingToggle}
                activeClassName="bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
              >
                Open to Work
              </FilterPill>
            </div>
          </div>
        </div>

        <div className="flex-none border-t border-white/10 px-6 py-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClearAll}
            disabled={!hasActive}
            className="text-xs font-semibold font-sans text-zinc-500 hover:text-white transition-colors disabled:opacity-30 disabled:pointer-events-none"
          >
            Clear all
          </button>
          <SheetClose asChild>
            <button
              type="button"
              className="h-9 px-5 rounded-md bg-primary text-white text-xs font-bold uppercase tracking-wide hover:bg-primary/90 transition-colors font-sans"
            >
              Done
            </button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
